import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/database.types";
import { isRequestEligibleForMatch } from "./matchEligibility";
import {
  ITEM_MATCH_LISTING_SELECT,
  itemRowToMatchListing,
} from "./matchListingFromItem";
import { rankScoredMatches } from "./matchRank";
import { scoreListingAgainstRequest } from "./matchScore";
import type { MatchRequestInput, RankedMatches } from "./matchTypes";

type ServiceClient = SupabaseClient<Database>;

const INVENTORY_POOL_LIMIT = 150;

/**
 * Pull: score approved inventory in the request's category → ranked bands.
 */
export async function findMatchesForRequest(
  service: ServiceClient,
  request: MatchRequestInput,
  options?: { maxPerBand?: number; maxTotal?: number },
): Promise<RankedMatches> {
  if (!isRequestEligibleForMatch(request)) {
    return {
      strong: [],
      soft: [],
      exploratory: [],
      overBudget: [],
      ordered: [],
    };
  }

  const { data, error } = await service
    .from("items")
    .select(ITEM_MATCH_LISTING_SELECT)
    .eq("status", "approved")
    .eq("category", request.category)
    .order("created_at", { ascending: false })
    .limit(INVENTORY_POOL_LIMIT);

  if (error) {
    throw new Error(error.message);
  }

  const scored = [];
  for (const row of data ?? []) {
    const match = scoreListingAgainstRequest(
      request,
      itemRowToMatchListing(row),
    );
    if (match) scored.push(match);
  }

  return rankScoredMatches(scored, options);
}

/**
 * Attach first image URLs for pull UI (best-effort).
 */
export async function attachListingThumbnails(
  service: ServiceClient,
  ranked: RankedMatches,
): Promise<RankedMatches> {
  const ids = ranked.ordered.map((m) => m.listing.id);
  if (ids.length === 0) return ranked;

  const { data, error } = await service
    .from("item_images")
    .select("item_id,url,is_main,position")
    .in("item_id", ids)
    .order("position", { ascending: true });

  if (error || !data) return ranked;

  const firstByItem = new Map<string, string>();
  // Prefer is_main, else first by position order from query
  for (const row of data) {
    if (!row.item_id || !row.url) continue;
    if (row.is_main) {
      firstByItem.set(row.item_id, row.url);
    }
  }
  for (const row of data) {
    if (!row.item_id || !row.url) continue;
    if (!firstByItem.has(row.item_id)) {
      firstByItem.set(row.item_id, row.url);
    }
  }

  const withImage = (m: (typeof ranked.ordered)[number]) => ({
    ...m,
    listing: {
      ...m.listing,
      imageUrl: firstByItem.get(m.listing.id) ?? m.listing.imageUrl ?? null,
    },
  });

  return {
    strong: ranked.strong.map(withImage),
    soft: ranked.soft.map(withImage),
    exploratory: ranked.exploratory.map(withImage),
    overBudget: ranked.overBudget.map(withImage),
    ordered: ranked.ordered.map(withImage),
  };
}
